<template>
  <div
    :class="[$attrs.class, 'input-wrapper']"
    :style="$attrs.style"
  >
    <component
      :is="multiline ? 'v-textarea' : 'v-text-field'"
      v-bind="inputBindings"
      :variant="props.variant"
      :clearable="clearable"
      :model-value="modelValue"
      :class="$attrs.class"
      @focus="$emit('focus', $event)"
      @click:clear="$emit('click:clear', $event)"
      @update:modelValue="onInput"
    >
    <template #append-inner>
        <v-icon
          v-if="showSearchIcon"
          class="mr-2"
          icon="mdi-magnify"
        />

        <v-icon
          v-if="isPassword"
          class="mr-2"
          :icon="showPw ? 'mdi-eye' : 'mdi-eye-off'"
          style="cursor: pointer"
          @click.stop="togglePw"
        />
      </template>

      <slot />
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount, useAttrs } from 'vue'

defineOptions({ name: 'InputText' })

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object, Array],
    default: () => '',
  },
  multiline: { type: Boolean, default: false },
  clearable: { type: Boolean, default: false },
  variant: { type: String, default: 'outlined' },
})

const emit = defineEmits(['update:modelValue', 'focus', 'click:clear'])
const attrs = useAttrs()

const originalType = ref('text')
const showPw = ref(false)

onBeforeMount(() => {
  originalType.value = String(attrs?.type ?? 'text')
})

const isPassword = computed(() => originalType.value === 'password')
const showSearchIcon = computed(() => originalType.value === 'search')

function togglePw() {
  showPw.value = !showPw.value
}

function onInput(val: unknown) {
  emit("update:modelValue", val ?? "");
}

const inputBindings = computed(() => {
  const bindings: Record<string, any> = {}

  for (const [key, val] of Object.entries(attrs)) {
    if (!['class', 'style'].includes(key) && !key.startsWith('on')) {
      bindings[key] = val
    }
  }

  bindings.type = originalType.value === 'password' && showPw.value ? 'text' : originalType.value

  return bindings
})
</script>
<style lang="scss">
.input-wrapper {
  max-width: 100%;
}

.v-field {
  max-width: 100%;
}

.v-field__input {
  font-size:1.125rem;
  min-height: 44px;
  width: 100%;
  min-width: 240px;
  max-width: 100%;
  flex: 0 0 auto;
  padding-bottom: unset;
  box-sizing: border-box;
}

.v-field--appended {
  --v-field-padding-end: 16px !important;
}

::v-deep(.v-input.v-input--horizontal .v-label) {
  font-size: 1.125rem !important;
}

.v-field::after, .v-field::before {
  bottom: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
}

</style>
